import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import goodsAuditQueueApi from '../../api/goodsAuditQueue';
import { message } from 'antd';

// 时间数组格式化工具函数
const formatTimeArray = (timeArray) => {
  if (!timeArray || !Array.isArray(timeArray) || timeArray.length < 3) {
    return '未知时间';
  }
  const [year, month, day, hour = 0, minute = 0, second = 0] = timeArray;
  const padZero = (num) => num.toString().padStart(2, '0');
  return `${year}-${padZero(month)}-${padZero(day)} ${padZero(hour)}:${padZero(minute)}:${padZero(second)}`;
};

// 分页查询商品审核队列
export const fetchGoodsAuditQueues = createAsyncThunk(
  'goodsAuditQueue/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await goodsAuditQueueApi.pageGoodsAuditQueues(params);
      // 转换数据结构
      const transformedRecords = response.data.records.map(item => ({
        ...item.goodsAuditQueue,
        goodsImageAuditQueues: item.goodsImageAuditQueues,
        createTimeFormatted: formatTimeArray(item.goodsAuditQueue.createTime),
        updateTimeFormatted: formatTimeArray(item.goodsAuditQueue.updateTime)
      }));
      return {
        ...response.data,
        records: transformedRecords
      };
    } catch (error) {
      message.error('获取商品审核队列列表失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 获取商品审核队列详情
export const fetchGoodsAuditQueueDetail = createAsyncThunk(
  'goodsAuditQueue/fetchDetail',
  async (goodsAuditQueueId, { rejectWithValue }) => {
    try {
      const response = await goodsAuditQueueApi.getGoodsAuditQueueDetail(goodsAuditQueueId);
      // 转换数据结构
      const transformedData = {
        ...response.data.goodsAuditQueue,
        goodsImageAuditQueues: response.data.goodsImageAuditQueues,
        createTimeFormatted: formatTimeArray(response.data.goodsAuditQueue.createTime),
        updateTimeFormatted: formatTimeArray(response.data.goodsAuditQueue.updateTime)
      };
      return transformedData;
    } catch (error) {
      message.error('获取商品审核队列详情失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 添加商品审核队列
export const addGoodsAuditQueue = createAsyncThunk(
  'goodsAuditQueue/add',
  async (goodsAuditQueueData, { dispatch, rejectWithValue }) => {
    try {
      const response = await goodsAuditQueueApi.addGoodsAuditQueue(goodsAuditQueueData);
      message.success('商品审核队列添加成功');
      dispatch(fetchGoodsAuditQueues());
      return response.data;
    } catch (error) {
      message.error('商品审核队列添加失败');
      return rejectWithValue(error.response.data);
    }
  }
);

// 更新商品审核队列
export const updateGoodsAuditQueue = createAsyncThunk(
  'goodsAuditQueue/update',
  async (goodsAuditQueueData, { dispatch, rejectWithValue }) => {
    try {
      const response = await goodsAuditQueueApi.updateGoodsAuditQueue(goodsAuditQueueData);
      message.success('商品审核队列更新成功');
      dispatch(fetchGoodsAuditQueues());
      return response.data;
    } catch (error) {
      message.error('商品审核队列更新失败');
      return rejectWithValue(error.response.data);
    }
  }
);

// 删除商品审核队列
export const deleteGoodsAuditQueue = createAsyncThunk(
  'goodsAuditQueue/delete',
  async (goodsAuditQueueId, { dispatch, rejectWithValue }) => {
    try {
      const response = await goodsAuditQueueApi.deleteGoodsAuditQueue(goodsAuditQueueId);
      message.success('商品审核队列删除成功');
      dispatch(fetchGoodsAuditQueues());
      return response.data;
    } catch (error) {
      message.error('商品审核队列删除失败');
      return rejectWithValue(error.response.data);
    }
  }
);

// 审核商品
export const auditGoods = createAsyncThunk(
  'goodsAuditQueue/audit',
  async (auditData, { dispatch, rejectWithValue }) => {
    try {
      const response = await goodsAuditQueueApi.auditGoods(auditData);
      message.success('商品审核成功');
      dispatch(fetchGoodsAuditQueues());
      return response.data;
    } catch (error) {
      message.error('商品审核失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

const goodsAuditQueueSlice = createSlice({
  name: 'goodsAuditQueue',
  initialState: {
    goodsAuditQueues: [],
    currentGoodsAuditQueue: null,
    total: 0,
    page: 1,
    pageSize: 10,
    loading: false,
    error: null
  },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取列表
      .addCase(fetchGoodsAuditQueues.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGoodsAuditQueues.fulfilled, (state, action) => {
        state.loading = false;
        state.goodsAuditQueues = action.payload.records;
        state.total = action.payload.total;
        state.page = action.payload.current;
        state.pageSize = action.payload.size;
      })
      .addCase(fetchGoodsAuditQueues.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 获取详情
      .addCase(fetchGoodsAuditQueueDetail.fulfilled, (state, action) => {
        state.currentGoodsAuditQueue = action.payload;
      });
  }
});

export const { 
  setPage, 
  setPageSize 
} = goodsAuditQueueSlice.actions;

export default goodsAuditQueueSlice.reducer; 